import React from 'react';
import { clsx } from 'clsx';
import { dashboardPanel, dashboardPanelHover } from './dashboardStyles';
import type { DemoAudience, DemoCategory } from '../../data/demoGraphs';
import { DashboardIcon } from './DashboardIcon';
import { getCategoryConfig, stripEmoji } from './dashboardIconConfig';

export type DemoScenarioCardData = {
    id: string;
    title: string;
    description: string;
    category: DemoCategory;
    tags: string[];
    address?: string;
    upi?: string;
    priority?: boolean;
    startHereOrder?: number;
    demoType?: DemoAudience;
};

type Props = {
    scenario: DemoScenarioCardData;
    onClick: () => void;
};

const MAX_TAGS = 3;

const focusRing =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

function addressTooltip(scenario: DemoScenarioCardData): string | undefined {
    if (scenario.upi) return scenario.upi;
    if (scenario.address) return scenario.address;
    return undefined;
}

function overflowTagsTooltip(tags: string[], max: number): string | undefined {
    if (tags.length <= max) return undefined;
    return tags.slice(max).map(stripEmoji).join(', ');
}

export const DemoScenarioCard: React.FC<Props> = ({ scenario, onClick }) => {
    const categoryConfig = getCategoryConfig(scenario.category);
    const displayTitle = stripEmoji(scenario.title);
    const displayTags = scenario.tags.map(stripEmoji).filter(Boolean);
    const visibleTags = displayTags.slice(0, MAX_TAGS);
    const overflowCount = displayTags.length - MAX_TAGS;
    const overflowTooltip = overflowTagsTooltip(displayTags, MAX_TAGS);
    const addressHint = addressTooltip(scenario);

    return (
        <button
            type="button"
            onClick={onClick}
            title={addressHint}
            aria-label={`Open demo: ${displayTitle}`}
            className={clsx(
                'group relative overflow-hidden rounded-xl text-left w-full',
                dashboardPanel,
                dashboardPanelHover,
                'active:scale-[0.99]',
                focusRing,
            )}
        >
            <div className="relative p-5">
                <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
                    {scenario.startHereOrder != null && (
                        <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary dark:bg-primary/15">
                            Guided · Step {scenario.startHereOrder}
                        </span>
                    )}
                    {scenario.startHereOrder == null && scenario.priority && (
                        <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400">
                            Featured
                        </span>
                    )}
                    {scenario.demoType && (
                        <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground capitalize">
                            {scenario.demoType === 'feature' ? 'Tutorial' : 'Investigation'}
                        </span>
                    )}
                </div>
                <DashboardIcon
                    variant="tile"
                    tone={categoryConfig.tone}
                    icon={categoryConfig.icon}
                    className="mb-3"
                />
                <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors leading-snug">
                    {displayTitle}
                </h3>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2 leading-relaxed">
                    {scenario.description}
                </p>
                {displayTags.length > 0 && (
                    <div className="flex flex-wrap gap-1" aria-label="Scenario tags">
                        {visibleTags.map((tag) => (
                            <span
                                key={tag}
                                className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground"
                            >
                                {tag}
                            </span>
                        ))}
                        {overflowCount > 0 && (
                            <span
                                className="text-xs px-2 py-0.5 rounded-full bg-secondary/70 text-muted-foreground cursor-default"
                                title={overflowTooltip}
                                aria-label={`${overflowCount} more tags: ${overflowTooltip}`}
                            >
                                +{overflowCount}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </button>
    );
};
